import type { View } from "@valuemap/core";
import { useEffect, useMemo, useRef, useState } from "react";
import { AccountPicker, NO_SELECTION, type Selection } from "./AccountPicker.js";
import {
  ApiError,
  addMeAsEditor,
  archiveMap,
  duplicateMap,
  exportUrl,
  fetchMap,
  finaliseMap,
  importIntoMap,
  renameMap,
  setWorkshop,
  type MapDetail,
  type Me,
} from "./api.js";
import { AppHeader } from "./AppHeader.js";
import { Board } from "./Board.js";
import { EditView } from "./EditView.js";
import { HistoryPanel } from "./HistoryPanel.js";
import { FieldLocksContext, type FieldLocksValue } from "./live/FieldLocks.js";
import type { ClientState } from "./live/map-client.js";
import { useMapClient } from "./live/useMapClient.js";
import live from "./Live.module.css";
import { go } from "./nav.js";
import { PresenceBar } from "./Presence.js";
import { PrioritiseView } from "./PrioritiseView.js";
import { QuantifyView } from "./QuantifyView.js";
import { SharePanel } from "./SharePanel.js";
import ui from "./Ui.module.css";
import { ConfirmDialog, Modal } from "./ui/Modal.js";
import { MoreMenu } from "./ui/MoreMenu.js";
import { Toast } from "./ui/Toast.js";
import styles from "./Workspace.module.css";

const MODES: ReadonlyArray<[View, string]> = [
  ["edit", "Edit"],
  ["quantify", "Quantify"],
  ["prioritise", "Prioritise"],
  ["present", "Present"],
];

/** The save status in the toolbar (brief 8.1): what Salesforce holds, and whether it is behind. */
export function saveText(s: ClientState): string {
  if (s.phase === "error") return "";
  if (s.phase === "connecting") return "Connecting…";
  if (s.phase === "reconnecting") return "Reconnecting…";
  if (s.unsaved) return "Not yet saved to Salesforce";
  if (s.pending > 0 || s.revision > s.savedRevision) return "Saving…";
  if (s.savedAt) {
    const time = new Date(s.savedAt).toLocaleTimeString("en-AU", {
      hour: "numeric",
      minute: "2-digit",
    });
    return `Saved to Salesforce ${time}`;
  }
  return "Saved to Salesforce";
}

type Dialog = "import" | "duplicate" | "archive" | "share" | "finalise" | "history" | null;

/**
 * One value map: the four reference views (Edit, Quantify, Prioritise, Present) over a live
 * connection that saves to Salesforce, shared with whoever else is in the map.
 *
 * Staff see the Salesforce context (reference, Account, Opportunity) from the staff API. Client
 * participants never call that API: everything they see comes over the socket, which carries no
 * Salesforce detail (brief 8.2).
 */
export function Workspace({ id, me }: { id: string; me: Me }) {
  const isGuest = me.type === "guest";
  const { client, state } = useMapClient(id);
  const [detail, setDetail] = useState<MapDetail | null | undefined>(undefined);
  const [detailError, setDetailError] = useState(false);
  const [detailVersion, setDetailVersion] = useState(0);
  const [mode, setMode] = useState<View>("edit");
  const [activeId, setActiveId] = useState<string | null>(null);
  const [following, setFollowing] = useState(true);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [pendingFile, setPendingFile] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ id: number; text: string; at: number } | null>(null);
  const [draftTitle, setDraftTitle] = useState<string | null>(null);
  const [copyTo, setCopyTo] = useState<Selection>(NO_SELECTION);
  const [copyError, setCopyError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const messageId = useRef(0);

  const say = (text: string) => setMessage({ id: ++messageId.current, text, at: Date.now() });

  useEffect(() => {
    if (isGuest) return;
    const ctl = new AbortController();
    fetchMap(id, ctl.signal).then(setDetail, (err: unknown) => {
      if ((err as Error).name !== "AbortError") setDetailError(true);
    });
    return () => ctl.abort();
  }, [id, isGuest, detailVersion]);

  // One toast: whichever is newer of this page's messages and the live connection's (a rejected
  // edit, for example).
  const latest = [
    message && { ...message, from: "page" },
    state.notice && { ...state.notice, from: "live" },
  ]
    .filter((m) => m !== null && m !== undefined)
    .sort((a, b) => b.at - a.at)[0];
  const toast = latest ? { key: `${latest.from}-${latest.id}`, text: latest.text } : null;

  // Warn before leaving with edits that are not yet in Salesforce.
  const unsafe = state.pending > 0 || state.unsaved || state.revision > state.savedRevision;
  useEffect(() => {
    if (!unsafe) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [unsafe]);

  const map = state.map;
  const settings = state.settings;
  const canEdit = settings?.canEdit ?? false;
  const canDelete = settings?.canDelete ?? false;
  const canLead = settings?.canLead ?? false;
  const status = settings?.status ?? detail?.status ?? "Draft";
  const title = settings?.title ?? detail?.title ?? "";

  // Follow me: while someone else leads and this person has not opted out, show what they show.
  const leader = state.participants.find((p) => p.leading && p.id !== state.me);
  const amLeading = state.participants.some((p) => p.leading && p.id === state.me);
  const followingLeader = following && leader !== undefined;
  const shownMode: View = followingLeader ? leader.view : mode;
  const wanted = followingLeader && leader.goalId ? leader.goalId : activeId;
  const active = map?.goals.some((g) => g.id === wanted) ? wanted : (map?.goals[0]?.id ?? null);

  // Tell the room where this person is.
  useEffect(() => {
    if (state.phase === "ready") client.setPresence(shownMode, active ?? undefined);
  }, [client, state.phase, shownMode, active]);

  const chooseMode = (m: View) => {
    // Choosing your own view stops following, from where the leader was.
    if (followingLeader) {
      setFollowing(false);
      if (leader.goalId) setActiveId(leader.goalId);
    }
    setMode(m);
  };
  const chooseGoal = (goalId: string | null) => {
    if (followingLeader) {
      setFollowing(false);
      setMode(leader.view);
    }
    setActiveId(goalId);
  };

  const locks = useMemo<FieldLocksValue>(
    () => ({
      lockedBy: (path) => {
        const l = state.locks[path];
        return l && l.holder.id !== state.me ? l.holder.name : null;
      },
      focus: (path) => client.lockField(path),
      blur: (path) => client.unlockField(path),
    }),
    [client, state.locks, state.me],
  );

  const dispatch = (...args: Parameters<typeof client.dispatch>) => {
    client.dispatch(...args);
  };

  async function run(task: () => Promise<void>, failure: string) {
    setBusy(true);
    try {
      await task();
    } catch (err) {
      say(err instanceof ApiError ? err.message : failure);
    } finally {
      setBusy(false);
    }
  }

  async function chooseFile(file: File | undefined) {
    if (!file) return;
    try {
      setPendingFile(JSON.parse(await file.text()));
      setDialog("import");
    } catch {
      say("That file could not be read. Choose a JSON export from the Value Map Builder.");
    }
  }

  const print = () => {
    chooseMode("present");
    setTimeout(() => window.print(), 120);
  };

  const perms = detail?.permissions;

  const header = (
    <AppHeader me={me}>
      <div className={ui.modes} role="group" aria-label="View mode">
        {MODES.map(([m, label]) => (
          <button
            key={m}
            type="button"
            aria-pressed={shownMode === m}
            onClick={() => chooseMode(m)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className={ui.actions}>
        {perms?.manageParticipants && (
          <button type="button" className={ui.btn} onClick={() => setDialog("share")}>
            Share
          </button>
        )}
        <button type="button" className={ui.btn} onClick={print}>
          Print
        </button>
        {/* Less frequent staff actions, so the toolbar fits on one row at 1366 pixels. */}
        {detail && (
          <MoreMenu>
            {perms?.history && (
              <button type="button" onClick={() => setDialog("history")}>
                History
              </button>
            )}
            {perms?.export && (
              <a href={exportUrl(id)} download>
                Export
              </a>
            )}
            {perms?.import && status === "Draft" && (
              <button type="button" onClick={() => fileInput.current?.click()}>
                Import
              </button>
            )}
            <button type="button" onClick={() => setDialog("duplicate")}>
              Duplicate
            </button>
            {perms?.finalise && status !== "Final" && (
              <button type="button" onClick={() => setDialog("finalise")}>
                Finalise
              </button>
            )}
            {perms?.archive && (
              <button type="button" onClick={() => setDialog("archive")}>
                Archive
              </button>
            )}
          </MoreMenu>
        )}
      </div>
      <PresenceBar participants={state.participants} me={state.me} />
      <div className={ui.saveState} role="status" aria-live="polite">
        {saveText(state)}
      </div>
    </AppHeader>
  );

  const home = (
    <a href="/" className={styles.back}>
      ← {isGuest ? "Your value maps" : "All value maps"}
    </a>
  );

  if (detail === null || state.phase === "error" || detailError) {
    return (
      <>
        <AppHeader me={me} />
        <nav className={`${styles.context} no-print`} aria-label="Value map">
          {home}
        </nav>
        <p className={styles.note} role="alert">
          {detail === null
            ? "That value map could not be found."
            : (state.failure ??
              "The value map could not be loaded from Salesforce. Please refresh the page.")}
        </p>
      </>
    );
  }

  const readOnlyNote = (() => {
    if (!map || canEdit) return null;
    if (status === "Archived") return "This value map is archived and read-only.";
    if (isGuest && status === "Final") return "This value map is final and read-only.";
    if (isGuest) return "You can view this value map but not change it.";
    return "You can view this value map. To edit it, add yourself as an editor.";
  })();

  return (
    <FieldLocksContext.Provider value={locks}>
      {header}
      <input
        ref={fileInput}
        type="file"
        accept="application/json,.json"
        hidden
        onChange={(e) => {
          void chooseFile(e.target.files?.[0]);
          e.target.value = "";
        }}
      />

      <nav className={`${styles.context} no-print`} aria-label="Value map">
        {home}
        {detail ? (
          <input
            className={styles.titleInput}
            aria-label="Value map title"
            value={draftTitle ?? title}
            readOnly={!perms?.edit}
            maxLength={255}
            onFocus={() => setDraftTitle(title)}
            onChange={(e) => setDraftTitle(e.target.value)}
            onBlur={() => {
              const next = (draftTitle ?? "").trim();
              setDraftTitle(null);
              if (!next || next === title) return;
              void renameMap(id, next).catch((err: unknown) =>
                say(err instanceof ApiError ? err.message : "The title could not be saved."),
              );
            }}
          />
        ) : (
          title && <h1 className={styles.guestTitle}>{title}</h1>
        )}
        {detail && (
          <>
            <span className={styles.item}>{detail.reference}</span>
            {detail.accountName && <span className={styles.item}>{detail.accountName}</span>}
            {detail.opportunityName && (
              <span className={styles.item}>{detail.opportunityName}</span>
            )}
          </>
        )}
        <span className={styles.status}>{status}</span>
      </nav>

      {map && (canLead || leader || status === "Live workshop") && (
        <div className={`${live.workshop} no-print`} role="region" aria-label="Workshop">
          {status === "Live workshop" && <strong>Live workshop</strong>}
          {leader &&
            (following ? (
              <span>
                Following {leader.name}.{" "}
                <button
                  type="button"
                  className={live.linkBtn}
                  onClick={() => chooseMode(shownMode)}
                >
                  Stop following
                </button>
              </span>
            ) : (
              <span>
                {leader.name} is leading.{" "}
                <button type="button" className={live.linkBtn} onClick={() => setFollowing(true)}>
                  Follow
                </button>
              </span>
            ))}
          {amLeading && <span>Everyone following sees your view and goal.</span>}
          <span className={live.spacer} />
          {canLead && (
            <>
              <button
                type="button"
                className={`${ui.btn} ${live.toggle}`}
                aria-pressed={amLeading}
                onClick={() => client.follow(!amLeading)}
              >
                {amLeading ? "Stop leading" : "Follow me"}
              </button>
              {(status === "Draft" || status === "Live workshop") && (
                <button
                  type="button"
                  className={ui.btn}
                  disabled={busy}
                  onClick={() =>
                    void run(async () => {
                      const live_ = status !== "Live workshop";
                      const r = await setWorkshop(id, live_);
                      if (!live_ && amLeading) client.follow(false);
                      say(
                        live_
                          ? "Workshop started"
                          : r.version
                            ? `Workshop ended. Version ${r.version} saved.`
                            : "Workshop ended",
                      );
                    }, "The workshop could not be changed.")
                  }
                >
                  {status === "Live workshop" ? "End workshop" : "Start workshop"}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {(state.phase === "reconnecting" || state.phase === "connecting") && map && (
        <div className={`${styles.banner} no-print`} role="status">
          Reconnecting. Your changes are kept and will be sent when the connection is back.
        </div>
      )}
      {state.unsaved && (
        <div className={`${styles.banner} ${styles.warn} no-print`} role="alert">
          Salesforce could not be reached. Your changes are safe and will be saved when it is back.
        </div>
      )}
      {readOnlyNote && (
        <div className={`${styles.banner} no-print`} role="note">
          {readOnlyNote}
          {perms?.selfAddAsEditor && (
            <>
              {" "}
              <button
                type="button"
                className={live.linkBtn}
                disabled={busy}
                onClick={() =>
                  void run(async () => {
                    await addMeAsEditor(id);
                    setDetailVersion((v) => v + 1);
                    say("You can now edit this value map");
                  }, "You could not be added as an editor.")
                }
              >
                Add me as an editor
              </button>
            </>
          )}
        </div>
      )}

      {!map && <p className={styles.note}>Loading…</p>}

      {map && shownMode === "edit" && (
        <EditView
          map={map}
          canEdit={canEdit}
          canDelete={canDelete}
          dispatch={dispatch}
          activeId={active}
          onSelect={chooseGoal}
        />
      )}
      {map && shownMode === "quantify" && (
        <QuantifyView map={map} canEdit={canEdit} canDelete={canDelete} dispatch={dispatch} />
      )}
      {map && shownMode === "prioritise" && (
        <PrioritiseView map={map} canEdit={canEdit} canDelete={canDelete} dispatch={dispatch} />
      )}
      {map && shownMode === "present" && <Board map={map} />}
      {/* Printing from any view prints the Present board, as in the reference tool. */}
      {map && shownMode !== "present" && (
        <div className="print-only">
          <Board map={map} />
        </div>
      )}

      {dialog === "share" && (
        <SharePanel mapId={id} onClose={() => setDialog(null)} onMessage={say} />
      )}

      {dialog === "import" && (
        <ConfirmDialog
          title="Replace this value map?"
          message="Everything in this value map will be replaced by the contents of the file. This cannot be undone."
          confirmLabel="Replace"
          busy={busy}
          onCancel={() => {
            setDialog(null);
            setPendingFile(null);
          }}
          onConfirm={() =>
            void run(async () => {
              const { warnings } = await importIntoMap(id, pendingFile);
              setDialog(null);
              setPendingFile(null);
              setActiveId(null);
              say(warnings.length ? `Map imported. ${warnings.join(" ")}` : "Map imported");
            }, "The file could not be imported.")
          }
        />
      )}

      {dialog === "finalise" && (
        <ConfirmDialog
          title="Finalise this value map?"
          message="A version is saved and the map becomes Final. Client participants can still view and print it but can no longer change it. SalesFix editors can still make corrections."
          confirmLabel="Finalise"
          busy={busy}
          onCancel={() => setDialog(null)}
          onConfirm={() =>
            void run(async () => {
              const r = await finaliseMap(id);
              if (amLeading) client.follow(false);
              setDialog(null);
              setDetailVersion((v) => v + 1);
              say(`Value map finalised. Version ${r.version} saved.`);
            }, "The value map could not be finalised.")
          }
        />
      )}

      {dialog === "history" && (
        <HistoryPanel
          mapId={id}
          canSave={perms?.saveVersion ?? false}
          canDownload={perms?.export ?? false}
          onClose={() => setDialog(null)}
          onMessage={say}
        />
      )}

      {dialog === "archive" && (
        <ConfirmDialog
          title="Archive this value map?"
          message="Archived maps are read-only and hidden from the home page. Anything not yet saved is written to Salesforce first."
          confirmLabel="Archive"
          busy={busy}
          onCancel={() => setDialog(null)}
          onConfirm={() =>
            void run(async () => {
              await archiveMap(id);
              go("/");
            }, "The value map could not be archived.")
          }
        />
      )}

      {dialog === "duplicate" && (
        <Modal title="Duplicate this value map" onClose={() => setDialog(null)}>
          <p>Copy the content to a new value map for another Account. You will own the copy.</p>
          <AccountPicker selection={copyTo} onChange={setCopyTo} />
          {copyError && (
            <p role="alert" className={styles.copyError}>
              {copyError}
            </p>
          )}
          <div className={ui.row}>
            <button type="button" className={ui.btn} onClick={() => setDialog(null)}>
              Cancel
            </button>
            <button
              type="button"
              className={`${ui.btn} ${ui.primary}`}
              disabled={!copyTo.account || busy}
              onClick={() => {
                if (!copyTo.account) return;
                setCopyError(null);
                setBusy(true);
                duplicateMap(id, {
                  accountId: copyTo.account.id,
                  opportunityId: copyTo.opportunityId || undefined,
                }).then(
                  ({ id: copyId }) => go(`/maps/${encodeURIComponent(copyId)}`),
                  (err: unknown) => {
                    setCopyError(
                      err instanceof ApiError ? err.message : "The copy could not be created.",
                    );
                    setBusy(false);
                  },
                );
              }}
            >
              Create copy
            </button>
          </div>
        </Modal>
      )}

      <Toast message={toast} />
    </FieldLocksContext.Provider>
  );
}
